import { useEffect, useState } from 'react';
import { DeleteOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Table, TableProps, DatePicker, Space, message, Popconfirm } from 'antd';
import { getRequest, downloadRequest, deleteRequest, ResponseDataType } from 'api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface StudentErrorDataType {
  id: number;
  createdDate: number;
  fileName: string;
  errorFilePath: string;
}

interface ResponseInfoType {
  data: StudentErrorDataType[];
  total: number;
}

export const ErrorImportStudents = () => {
  const [errorList, setErrorList] = useState<StudentErrorDataType[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [dates, setDates] = useState<[string, string] | null>(null);
  
  const schoolId = localStorage.getItem('schoolId');

  const fetchData = async () => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      perPage: pageSize.toString(),
      schoolId: schoolId || '',
    });
    
    if (dates && dates[0] && dates[1]) {
      // API may expect timestamp or ISO string. Since we send string from RangePicker, 
      // let's send timestamp as Long in backend expects.
      queryParams.append('startDate', dayjs(dates[0]).startOf('day').valueOf().toString());
      queryParams.append('endDate', dayjs(dates[1]).endOf('day').valueOf().toString());
    }

    const url = `/cms/student/import-errors?${queryParams.toString()}`;

    try {
      const response: ResponseDataType<ResponseInfoType> = await getRequest(url);
      if (response && response.code === 200) {
        setErrorList(response.info?.data || []);
        setTotal(response.info?.total || 0);
      } else {
        setErrorList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, schoolId, dates]);

  const handleDelete = async (id: number) => {
    const response = await deleteRequest(`/cms/student/import-errors/${id}`);
    if (response && response.code === 200) {
      message.success('Xóa lịch sử lỗi thành công!');
      fetchData();
    } else {
      message.error(response?.msg || 'Xóa thất bại!');
    }
  };

  const columns: TableProps<StudentErrorDataType>['columns'] = [
    {
      title: 'STT',
      key: 'stt',
      width: '60px',
      render: (_text, _record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên file import',
      dataIndex: 'fileName',
      key: 'fileName',
      width: '350px',
    },
    {
      title: 'Thời điểm tải lên',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: '150px',
      render: (createdDate) =>
        new Date(createdDate).toLocaleString('vi-VN', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
    {
      title: 'Hành động',
      dataIndex: 'id',
      key: 'action',
      width: '200px',
      render: (id, record) => {
        const handleDownload = () => {
          downloadRequest(`/cms/student/download-error-file?id=${id}`, record.fileName || `error_file_${id}.xlsx`);
        };
        return (
          <Space size="middle">
            <span
              onClick={handleDownload}
              style={{ color: '#1890ff', cursor: 'pointer', fontWeight: '500' }}
            >
              Tải file
            </span>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa bản ghi này không?"
              onConfirm={() => handleDelete(id)}
              okText="Đồng ý"
              cancelText="Hủy"
            >
              <span
                style={{ color: '#ff4d4f', cursor: 'pointer', fontWeight: '500' }}
              >
                Xóa
              </span>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (pagination: any) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleDateChange = (val: any, formatString: [string, string]) => {
    setDates(val ? formatString : null);
    setPage(1); // Reset to page 1 on filter
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <RangePicker 
          onChange={handleDateChange} 
          format="YYYY-MM-DD"
          placeholder={['Từ ngày', 'Đến ngày']}
        />
      </div>
      <Table
        columns={columns}
        dataSource={errorList}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};
